import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { Escena } from './escenas';
import { CSS_ESCENAS } from './escenasCss';

type Traducir = (clave: string, opciones?: Record<string, unknown>) => string;

type DibujoProps = { t: Traducir };

/** Alto del cielo que ocupa la escena por encima del muelle. */
const SOBRE_MUELLE = 180;

/**
 * Escena divertida del puerto (maqueta que aprobó Vadik el 12-oct-2026). Se
 * dibuja encima del agua y del cielo pero debajo de los barcos, y no recibe
 * toques. Las medidas vienen de una maqueta de 360 px de ancho centrada.
 */
export function EscenaPuerto({ escena }: { escena: Escena }) {
  const { t } = useTranslation();
  const id = useId().replace(/:/g, '');
  const Dibujo = DIBUJOS[escena];
  return (
    <svg
      aria-hidden
      data-testid={`escena-${escena}`}
      className="pe-escena"
      style={{
        position: 'absolute',
        left: 0,
        top: `calc(max(230px, 40vh) + env(safe-area-inset-top) - ${SOBRE_MUELLE}px)`,
        width: '100%',
        height: 560,
        overflow: 'visible',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <style>{CSS_ESCENAS}</style>
      <defs>
        <linearGradient id={`luz-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffe9a8" stopOpacity=".55" />
          <stop offset="1" stopColor="#ffe9a8" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* La maqueta pone el muelle en y = 282. */}
      <g transform={`translate(0,${SOBRE_MUELLE - 282})`}>
        {escena === 'faro' && (
          // El faro está pegado a la izquierda, no centrado.
          <g transform="translate(30.5,196.5)">
            <polygon
              className="pe-haz"
              points="0,0 320,-26 320,36"
              fill={`url(#luz-${id})`}
            />
          </g>
        )}
        <svg x="50%" overflow="visible">
          <g transform="translate(-180,0)">
            <Dibujo t={t} />
          </g>
        </svg>
      </g>
    </svg>
  );
}

type BocadilloProps = {
  clase: string;
  x: number;
  y: number;
  texto: string;
  transform?: string;
  color?: string;
  fondo?: string;
  radio?: number;
  peso?: number;
  serif?: boolean;
};

/** Bocadillo que se ajusta a su texto para que nunca se salga. */
function Bocadillo({
  clase,
  x,
  y,
  texto,
  transform,
  color = '#0b1d2a',
  fondo = '#fff',
  radio = 10,
  peso = 700,
  serif = false,
}: BocadilloProps) {
  const textoRef = useRef<SVGTextElement>(null);
  // Hasta medir, un cálculo aproximado.
  const ancho = texto.length * 5.8 + 18;
  const [caja, setCaja] = useState({
    x: x - ancho / 2,
    y: y - 15,
    w: ancho,
    h: 22,
  });

  useLayoutEffect(() => {
    let vivo = true;
    const medir = () => {
      const el = textoRef.current;
      if (!vivo || !el || typeof el.getBBox !== 'function') {
        return;
      }
      const b = el.getBBox();
      if (b.width > 0) {
        setCaja({ x: b.x - 9, y: b.y - 5, w: b.width + 18, h: b.height + 10 });
      }
    };
    medir();
    void document.fonts?.ready.then(medir);
    return () => {
      vivo = false;
    };
  }, [texto]);

  return (
    <g className={`pe-bocadillo ${clase}`} transform={transform}>
      <rect
        x={caja.x}
        y={caja.y}
        width={caja.w}
        height={caja.h}
        rx={radio}
        fill={fondo}
      />
      <text
        ref={textoRef}
        x={x}
        y={y}
        textAnchor="middle"
        fontFamily={serif ? 'Fraunces, Georgia, serif' : 'Manrope, sans-serif'}
        fontWeight={peso}
        fontSize="10"
        fill={color}
      >
        {texto}
      </text>
    </g>
  );
}

const COLORES_FIESTA = [
  '#e0766b',
  '#8fd3b0',
  '#ffd76a',
  '#7fb2ff',
  '#c58fd3',
  '#f0a35c',
  '#8fd3b0',
];

function Multitud() {
  return (
    <g>
      {COLORES_FIESTA.map((color, i) => {
        const piel = i % 2 ? '#d9a77a' : '#f0c9a0';
        return (
          <g key={i} transform={`translate(${74 + i * 21},282)`}>
            <g className="pe-salta" style={{ animationDelay: `${-i * 0.17}s` }}>
              <circle cx="0" cy="-25" r="4" fill={piel} />
              <path d="M-4.5 -20 L4.5 -20 L4.5 -8 L-4.5 -8 Z" fill={color} />
              <path
                d="M-3 -8 L-4 0 M3 -8 L4 0"
                stroke="#3b5a7a"
                strokeWidth="2.4"
              />
              <path
                d="M-4 -19 L-9 -29 M4 -19 L9 -29"
                stroke={piel}
                strokeWidth="1.8"
              />
            </g>
          </g>
        );
      })}
    </g>
  );
}

function Barquito({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(0,486)">
        <g className="pe-velero">
          <path d="M-18 0 L18 0 L13 7 L-13 7 Z" fill="#7a5a43" />
          <rect x="-1" y="-26" width="2" height="26" fill="#cfe9dc" />
          <path d="M1 -24 L16 -2 L1 -2 Z" fill="#efd9a7" />
          <path d="M-1 -20 L-12 -2 L-1 -2 Z" fill="#efd9a7" opacity=".8" />
          <path
            d="M-30 9 q10 -4 20 0"
            stroke="#dff3e8"
            strokeOpacity=".6"
            fill="none"
          />
        </g>
      </g>
      <g transform="translate(250,560)">
        <g className="pe-ola-grande">
          <path
            d="M-30 10 q14 -24 30 -16 q-10 2 -6 10 q14 10 36 6"
            fill="#bfe3d0"
            fillOpacity=".7"
          />
        </g>
      </g>
      <g transform="translate(160,520)">
        <g className="pe-barquito">
          <path d="M-24 0 L24 0 L17 10 L-17 10 Z" fill="#a7744d" />
          <g transform="translate(-2,-6)">
            <line
              className="pe-remo"
              x1="-20"
              y1="0"
              x2="0"
              y2="0"
              stroke="#c49a6c"
              strokeWidth="2"
              transform="rotate(30)"
            />
          </g>
          <g transform="translate(2,0)">
            <circle cx="0" cy="-13" r="4" fill="#f0c9a0" />
            <path d="M-4 -8 L4 -8 L5 0 L-5 0 Z" fill="#e0766b" />
            <g className="pe-sombrero">
              <ellipse cx="0" cy="-16" rx="8.5" ry="2" fill="#f2cf6b" />
              <path
                d="M-4.6 -16 Q-4.6 -22.5 0 -22.5 Q4.6 -22.5 4.6 -16 Z"
                fill="#f2cf6b"
              />
              <rect
                x="-4.6"
                y="-18.4"
                width="9.2"
                height="1.9"
                fill="#d33b2f"
              />
            </g>
          </g>
          <Bocadillo
            clase="pe-b-paso"
            x={31}
            y={-35}
            texto={t('Coming through!')}
          />
          <Bocadillo clase="pe-b-ay" x={30} y={-39} texto={t('My hat!')} />
          <Bocadillo
            clase="pe-b-recu"
            x={42}
            y={-39}
            texto={t('This one stays with me!')}
          />
        </g>
      </g>
    </g>
  );
}

function Hucha({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(240,282)">
        <circle cx="0" cy="-20" r="4.5" fill="#f0c9a0" />
        <path d="M-5 -15 L5 -15 L5 0 L-5 0 Z" fill="#8fd3b0" />
        <path d="M-4 0 L-4 12 M4 0 L6 12" stroke="#3b5a7a" strokeWidth="3" />
        <line
          className="pe-lanza"
          x1="5"
          y1="-13"
          x2="13"
          y2="-20"
          stroke="#f0c9a0"
          strokeWidth="2"
        />
        <Bocadillo
          clase="pe-b-dia2"
          x={-25}
          y={-47}
          texto={t('Day 2: into the piggy bank')}
        />
      </g>
      <g transform="translate(250,262)">
        <ellipse
          className="pe-moneda-vuela"
          cx="0"
          cy="0"
          rx="3"
          ry="3"
          fill="#ffd76a"
          stroke="#c99a2e"
          strokeWidth=".6"
        />
      </g>
      <g transform="translate(292,322)">
        <circle
          className="pe-clinc"
          cx="0"
          cy="-12"
          r="8"
          fill="none"
          stroke="#ffd76a"
          strokeWidth="1.5"
        />
        <g className="pe-hucha">
          <ellipse cx="0" cy="0" rx="15" ry="10" fill="#f2a3b3" />
          <ellipse cx="14" cy="1" rx="4" ry="3.6" fill="#e98fa2" />
          <circle cx="13" cy="0.4" r=".7" fill="#7a3d4a" />
          <circle cx="15" cy="0.4" r=".7" fill="#7a3d4a" />
          <path d="M6 -8 L9 -14 L11 -7 Z" fill="#e98fa2" />
          <circle cx="8" cy="-3" r="1" fill="#2b1a20" />
          <rect
            x="-4"
            y="-10.5"
            width="8"
            height="1.8"
            rx=".9"
            fill="#7a3d4a"
          />
          <path
            d="M-15 -1 q-5 -3 -3 -6"
            stroke="#e98fa2"
            fill="none"
            strokeWidth="1.4"
          />
          <rect
            x="-16"
            y="3"
            width="32"
            height="7"
            fill="#14424a"
            opacity=".55"
          />
        </g>
        <Bocadillo
          clase="pe-b-oink"
          x={16}
          y={-29}
          texto={t('Oink. Thanks.')}
        />
      </g>
    </g>
  );
}

function Loro({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(180,282)">
        <circle cx="0" cy="-24" r="5" fill="#d9a77a" />
        <path
          d="M-5.5 -27 Q0 -33 5.5 -27 L5.5 -25 L-5.5 -25 Z"
          fill="#d33b2f"
        />
        <rect x="-1" y="-26" width="5" height="3" rx="1" fill="#1b232b" />
        <line
          x1="-5"
          y1="-25.5"
          x2="5"
          y2="-25.5"
          stroke="#1b232b"
          strokeWidth=".7"
        />
        <path d="M-6 -19 L6 -19 L6 -6 L-6 -6 Z" fill="#26313b" />
        <path d="M-6 -15 L6 -15" stroke="#ffd76a" strokeWidth="1" />
        <path d="M-3 -6 L-4 0 M3 -6 L3 0" stroke="#5a3b2a" strokeWidth="2.6" />
        <g transform="translate(-6,-22)">
          <path d="M-1 0 Q-6 -2 -6 -8 Q-2 -12 1 -8 Z" fill="#2e8b62" />
          <circle cx="-3" cy="-10" r="2.6" fill="#e0766b" />
          <path d="M-5.6 -10 L-7.6 -9 L-5.6 -8.6 Z" fill="#ffd76a" />
          <circle cx="-3.2" cy="-10.6" r=".6" fill="#0b1d2a" />
          <g className="pe-alas-loro">
            <path d="M-3 -6 Q2 -12 4 -5 Z" fill="#7fb2ff" />
          </g>
          <path d="M-2 0 L-1 6 L1 0 Z" fill="#7fb2ff" />
        </g>
        <g transform="translate(16,0)">
          <rect x="0" y="-9" width="20" height="9" rx="1.5" fill="#8a5a3a" />
          <path d="M0 -9 L3 -17 L23 -17 L20 -9 Z" fill="#6e4429" />
          <g fill="#ffd76a">
            <ellipse cx="6" cy="-10" rx="3" ry="1.4" />
            <ellipse cx="11" cy="-11" rx="3" ry="1.4" />
            <ellipse cx="15" cy="-10" rx="3" ry="1.4" />
            <ellipse cx="9" cy="-12.5" rx="3" ry="1.4" />
          </g>
          <path
            className="pe-brillo"
            d="M8 -20 l1 3 l3 1 l-3 1 l-1 3 l-1 -3 l-3 -1 l3 -1 Z"
            fill="#fff6c8"
          />
          <path
            className="pe-brillo"
            style={{ animationDelay: '-.7s' }}
            d="M17 -16 l.8 2 l2 .8 l-2 .8 l-.8 2 l-.8 -2 l-2 -.8 l2 -.8 Z"
            fill="#fff6c8"
          />
        </g>
        <Bocadillo
          clase="pe-b-loro"
          x={-10}
          y={-51}
          texto={t('Compound interest! Compound interest!')}
          color="#2e8b62"
          peso={800}
        />
        <Bocadillo
          clase="pe-b-pirata"
          x={-5}
          y={-51}
          texto={t('Yes, Paco. In the long run.')}
        />
      </g>
    </g>
  );
}

function Rebajas({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(262,282)">
        <circle cx="0" cy="-22" r="4.5" fill="#d9a77a" />
        <path d="M-4.5 -24 Q0 -30 4.5 -24 L5 -19 L-5 -19 Z" fill="#5a3b2a" />
        <path d="M-5 -17 L5 -17 L6 -6 L-6 -6 Z" fill="#c58fd3" />
        <path d="M-3 -6 L-4 0 M3 -6 L4 0" stroke="#3b5a7a" strokeWidth="2.6" />
        <Bocadillo
          clase="pe-b-nopres"
          x={-40}
          y={-49}
          texto={t("That wasn't in the budget.")}
        />
      </g>
      <g transform="translate(300,282)">
        <circle cx="0" cy="-20" r="4.5" fill="#f0c9a0" />
        <path d="M-5 -26 L5 -26 L4 -30 L-4 -30 Z" fill="#2e8b62" />
        <path d="M-5 -15 L5 -15 L5 0 L-5 0 Z" fill="#e0766b" />
        <path d="M-4 0 L-4 14 M4 0 L6 14" stroke="#3b5a7a" strokeWidth="3" />
        <g className="pe-cana2">
          <line
            x1="4"
            y1="-8"
            x2="40"
            y2="-38"
            stroke="#c49a6c"
            strokeWidth="1.6"
          />
        </g>
        <line
          x1="40"
          y1="-38"
          x2="40"
          y2="34"
          stroke="#dff3e8"
          strokeOpacity=".6"
          strokeWidth=".8"
        />
        <Bocadillo
          clase="pe-b-ganga"
          x={-55}
          y={-49}
          texto={t('Look! 70% off!')}
        />
      </g>
      <g transform="translate(340,318)">
        <g className="pe-bolsa">
          <path d="M-6 -4 L6 -4 L7 9 L-7 9 Z" fill="#e0766b" />
          <path
            d="M-3 -4 Q0 -10 3 -4"
            stroke="#fff"
            fill="none"
            strokeWidth="1.2"
          />
          <text
            x="0"
            y="6"
            textAnchor="middle"
            fontFamily="Manrope, sans-serif"
            fontWeight="800"
            fontSize="6.5"
            fill="#fff"
          >
            %
          </text>
        </g>
      </g>
    </g>
  );
}

function Comisiones({ t }: DibujoProps) {
  return (
    <g>
      <g className="pe-aleta">
        <g transform="translate(0,600)">
          <g className="pe-aleta-sube">
            <path d="M0 0 L12 -22 L22 0 Z" fill="#5d6f7c" />
            <text
              x="11"
              y="-4"
              textAnchor="middle"
              textLength="11"
              lengthAdjust="spacingAndGlyphs"
              fontFamily="Manrope, sans-serif"
              fontWeight="800"
              fontSize="6.5"
              fill="#fff"
            >
              {t('{{percent}}%', { percent: 2 })}
            </text>
            <path
              d="M-6 1 q14 -3 34 0"
              stroke="#8fd3b0"
              strokeOpacity=".5"
              fill="none"
            />
          </g>
        </g>
      </g>
      <g className="pe-aleta pe-b">
        <g transform="translate(0,640)">
          <g className="pe-aleta-sube">
            <path d="M24 0 L12 -22 L0 0 Z" fill="#4d5d69" />
            <text
              x="12"
              y="-4"
              textAnchor="middle"
              textLength="14"
              lengthAdjust="spacingAndGlyphs"
              fontFamily="Manrope, sans-serif"
              fontWeight="800"
              fontSize="6"
              fill="#fff"
            >
              {t('{{percent}}%', { percent: '1,8' })}
            </text>
            <path
              d="M-4 1 q14 -3 32 0"
              stroke="#8fd3b0"
              strokeOpacity=".5"
              fill="none"
            />
          </g>
        </g>
      </g>
      <g transform="translate(150,560)">
        <g className="pe-bote">
          <path d="M-28 0 L28 0 L20 11 L-20 11 Z" fill="#a7744d" />
          <text
            x="0"
            y="8"
            textAnchor="middle"
            textLength="30"
            lengthAdjust="spacingAndGlyphs"
            fontFamily="Fraunces, Georgia, serif"
            fontWeight="700"
            fontSize="6"
            fill="#f3e3b8"
          >
            <Trans>My portfolio</Trans>
          </text>
          <circle cx="0" cy="-13" r="4" fill="#f0c9a0" />
          <path d="M-4 -8 L4 -8 L5 0 L-5 0 Z" fill="#7fb2ff" />
          <Bocadillo
            clase="pe-b-com1"
            x={0}
            y={-31}
            texto={t('Away with you, fees!')}
          />
          <Bocadillo
            clase="pe-b-com2"
            x={0}
            y={-31}
            texto={t("I'm switching to an index fund.")}
          />
        </g>
      </g>
    </g>
  );
}

function Faro({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(140,282)">
        <g className="pe-sigilo">
          <g className="pe-mira">
            <circle cx="0" cy="-24" r="4.5" fill="#f0c9a0" />
          </g>
          <path d="M-5 -19 L5 -19 L5 -6 L-5 -6 Z" fill="#c58fd3" />
          <path
            d="M-3 -6 L-4 0 M3 -6 L4 0"
            stroke="#3b5a7a"
            strokeWidth="2.6"
          />
          <path d="M-12 -14 L-5 -14 L-4 -5 L-13 -5 Z" fill="#ffd76a" />
          <path
            d="M-10.5 -14 Q-8.5 -18 -6.5 -14"
            stroke="#c99a2e"
            fill="none"
          />
          <Bocadillo
            clase="pe-b-oferta"
            x={38}
            y={-45}
            texto={t('It was on sale!')}
          />
        </g>
      </g>
      <Bocadillo
        clase="pe-b-faro"
        x={55}
        y={15}
        texto={t('Uncategorized spending!')}
        transform="translate(52,160)"
        color="#c0392b"
        peso={800}
      />
    </g>
  );
}

function Nomina({ t }: DibujoProps) {
  return (
    <g>
      <path d="M60 236 Q140 256 220 236" stroke="#3a2a1f" fill="none" />
      <g>
        <circle className="pe-bombilla" cx="76" cy="241" r="3" fill="#e0766b" />
        <circle
          className="pe-bombilla"
          style={{ animationDelay: '-.5s' }}
          cx="100"
          cy="246"
          r="3"
          fill="#ffd76a"
        />
        <circle
          className="pe-bombilla"
          cx="124"
          cy="248"
          r="3"
          fill="#8fd3b0"
        />
        <circle
          className="pe-bombilla"
          style={{ animationDelay: '-.5s' }}
          cx="148"
          cy="248"
          r="3"
          fill="#7fb2ff"
        />
        <circle
          className="pe-bombilla"
          cx="172"
          cy="246"
          r="3"
          fill="#e0766b"
        />
        <circle
          className="pe-bombilla"
          style={{ animationDelay: '-.5s' }}
          cx="196"
          cy="242"
          r="3"
          fill="#ffd76a"
        />
      </g>
      <line
        x1="60"
        y1="210"
        x2="60"
        y2="282"
        stroke="#3a2a1f"
        strokeWidth="2"
      />
      <line
        x1="220"
        y1="210"
        x2="220"
        y2="282"
        stroke="#3a2a1f"
        strokeWidth="2"
      />
      <rect x="58" y="204" width="164" height="20" rx="3" fill="#2e8b62" />
      <text
        x="140"
        y="219"
        textAnchor="middle"
        textLength="146"
        lengthAdjust="spacingAndGlyphs"
        fontFamily="Fraunces, Georgia, serif"
        fontWeight="700"
        fontSize="10.5"
        fill="#fff6c8"
      >
        {t('Day 28: payday!')}
      </text>
      <Multitud />
      <g fontFamily="serif" fontSize="13" fill="#ffd76a">
        <text className="pe-nota-m" x="100" y="250">
          ♪
        </text>
        <text
          className="pe-nota-m"
          style={{ animationDelay: '-1s' }}
          x="150"
          y="252"
        >
          €
        </text>
        <text
          className="pe-nota-m"
          style={{ animationDelay: '-2s' }}
          x="190"
          y="250"
        >
          ♪
        </text>
      </g>
      <g transform="translate(262,282)">
        <circle cx="0" cy="-24" r="4.5" fill="#f0c9a0" />
        <rect x="-6" y="-29" width="12" height="2" fill="#26313b" />
        <path d="M-5 -19 L5 -19 L5 -6 L-5 -6 Z" fill="#e8eef7" />
        <path d="M-3 -6 L-4 0 M3 -6 L4 0" stroke="#26313b" strokeWidth="2.6" />
        <g className="pe-teclea">
          <rect x="-12" y="-16" width="8" height="10" rx="1" fill="#26313b" />
          <rect x="-11" y="-15" width="6" height="2.6" fill="#8fd3b0" />
        </g>
        <Bocadillo
          clase="pe-b-calc"
          x={-85}
          y={-92}
          texto={t('Before we party: give every euro a job.')}
        />
      </g>
    </g>
  );
}

function Emergencia({ t }: DibujoProps) {
  return (
    <g>
      <g className="pe-cae">
        <g className="pe-tumba">
          <g className="pe-balanceo">
            <g className="pe-abre">
              <path d="M-46 -44 Q0 -106 46 -44 Z" fill="#2e8b62" />
              <text
                x="0"
                y="-48"
                textAnchor="middle"
                textLength="64"
                lengthAdjust="spacingAndGlyphs"
                fontFamily="Manrope, sans-serif"
                fontWeight="800"
                fontSize="6.5"
                fill="#fff6c8"
              >
                {t('EMERGENCY FUND')}
              </text>
              <path
                d="M-46 -44 L-3 -20 M46 -44 L3 -20"
                stroke="#dff3e8"
                strokeWidth=".7"
              />
            </g>
            <circle cx="0" cy="-22" r="4.2" fill="#f0c9a0" />
            <path d="M-5 -17 L5 -17 L5 -6 L-5 -6 Z" fill="#ffd76a" />
            <path
              d="M-3 -6 L-4 0 M3 -6 L4 0"
              stroke="#3b5a7a"
              strokeWidth="2.6"
            />
          </g>
        </g>
        <Bocadillo
          clase="pe-b-lavadora"
          x={70}
          y={-25}
          texto={t('The washing machine broke!')}
        />
        <Bocadillo
          clase="pe-b-ahorre"
          x={-70}
          y={-41}
          texto={t('Good thing I saved.')}
        />
      </g>
    </g>
  );
}

function Botella({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(0,585)">
        <g className="pe-botella">
          <g className="pe-cabecea">
            <rect
              x="-12"
              y="-6"
              width="20"
              height="10"
              rx="4"
              fill="#5fae8a"
              fillOpacity=".85"
            />
            <rect x="8" y="-3" width="6" height="4" fill="#5fae8a" />
            <rect x="13" y="-3.5" width="3" height="5" fill="#a7744d" />
            <rect x="-8" y="-3" width="12" height="4" rx="1" fill="#f3e3b8" />
          </g>
          <Bocadillo
            clase="pe-b-sos"
            x={45}
            y={-26}
            texto={t('SOS: I overspent on Fun again')}
            color="#5a3b2a"
            fondo="#f3e3b8"
            radio={4}
            serif
          />
        </g>
      </g>
    </g>
  );
}

function Gaviota({ t }: DibujoProps) {
  return (
    <g>
      <g transform="translate(110,282)">
        <circle cx="0" cy="-20" r="4.5" fill="#d9a77a" />
        <path d="M-5 -15 L5 -15 L5 0 L-5 0 Z" fill="#7fb2ff" />
        <path d="M-4 0 L-4 12 M4 0 L6 12" stroke="#3b5a7a" strokeWidth="3" />
        <line
          className="pe-mano"
          x1="5"
          y1="-12"
          x2="16"
          y2="-6"
          stroke="#d9a77a"
          strokeWidth="2"
        />
        <g fill="#ffd76a" stroke="#c99a2e" strokeWidth=".6">
          <ellipse cx="22" cy="-2" rx="5" ry="1.8" />
          <ellipse cx="22" cy="-5" rx="5" ry="1.8" />
          <ellipse cx="22" cy="-8" rx="5" ry="1.8" />
          <ellipse
            className="pe-moneda-pila"
            cx="22"
            cy="-11"
            rx="5"
            ry="1.8"
          />
        </g>
        <Bocadillo
          clase="pe-b-eh"
          x={28}
          y={-47}
          texto={t('Hey! That was for the emergency fund!')}
        />
        <g transform="translate(22,-18)">
          <g className="pe-gaviota-l">
            <ellipse cx="0" cy="0" rx="9" ry="4.5" fill="#f4f7fb" />
            <circle cx="-8" cy="-3" r="3.4" fill="#f4f7fb" />
            <path d="M-11 -3 L-15 -2 L-11 -1.5 Z" fill="#f2b33d" />
            <circle cx="-8.6" cy="-3.8" r=".8" fill="#0b1d2a" />
            <g className="pe-aleteo">
              <path d="M-2 -2 Q4 -14 12 -10 Q6 -6 4 -1 Z" fill="#cfd8e6" />
            </g>
            <ellipse
              className="pe-moneda-robada"
              cx="-14"
              cy="0"
              rx="3.5"
              ry="1.4"
              fill="#ffd76a"
            />
          </g>
        </g>
      </g>
    </g>
  );
}
const DIBUJOS: Record<Escena, (props: DibujoProps) => ReactElement> = {
  aparta: Barquito,
  hucha: Hucha,
  loro: Loro,
  rebajas: Rebajas,
  comisiones: Comisiones,
  faro: Faro,
  nomina: Nomina,
  emergencia: Emergencia,
  botella: Botella,
  gaviota: Gaviota,
};
